import { useContext } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import {
  DisplayResultMessage,
  ExchangeName,
  GetAuthUrlRequest,
  LanguageNameSpaces,
  OpenConfirmModal,
  UsageType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { UseQuotaModal } from "@shared/features/settings/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { ConnectExchangeTabs } from "@shared/features/portfolio/types";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context";
import { useSelector } from "react-redux";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";

interface useQuickConnectFormProps {
  primaryExchange?: ExchangeName;
  trackAnalyticsConnectClicked: () => void;
  /** Every connect attempt, in or out of onboarding. */
  onConnectClicked?: () => void;
  isOnboarding?: boolean;
  openAuthURL: (url: string) => void;
  useQuotaModal: UseQuotaModal;
  openConfirmModal: OpenConfirmModal;
  redirectToWebQuickConnect?: (args: GetAuthUrlRequest) => void;
  displayResultMessage?: DisplayResultMessage;
}

const useQuickConnectForm = ({
  primaryExchange,
  trackAnalyticsConnectClicked,
  onConnectClicked,
  isOnboarding,
  openAuthURL,
  useQuotaModal,
  openConfirmModal,
  redirectToWebQuickConnect,
  displayResultMessage,
}: useQuickConnectFormProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const {
    isExternalConnectAccount,
    token: externalInviteToken,
    hasReadAccess,
  } = useContext(ExternalConnectAccountContext) || {};
  const { noFuturesAccountMessageData, quickConnectAlreadyUsedMessageData } =
    useContext(QuickConnectContext) || {};
  const {
    exchangeToClientName,
    accountName,
    setTab,
    handleOnSubmit,
    accountNameError,
    selectedSubExchanges,
  } = useContext(AccountFormContext)!;
  const {
    quotaModal,
    handleTriggerClick,
    isLoading: isLoadingQuotaCheck,
  } = useQuotaModal({
    usageType: UsageType.CLIENT,
  });
  const { getIsFree } = useContext(ExchangesMetadataContext) || {};
  const userAuthToken = useSelector(
    sharedLib.selectors.authentication.selectToken
  );

  const [getAuthUrl, { isLoading: getAuthUrlLoading }] =
    sharedLib.sharedApi.configuration.useLazyGetAuthUrlQuery();

  const handleConnectToExchange = () => {
    if (!primaryExchange || !exchangeToClientName || !accountName) return;
    const args: GetAuthUrlRequest = {
      nested_exchange: primaryExchange,
      exchange_to_client_name: exchangeToClientName,
      client_base_name: accountName,
    };
    if (externalInviteToken) {
      args.authToken = externalInviteToken;
    }
    if (isExternalConnectAccount && hasReadAccess) {
      args.user_auth_token = userAuthToken;
    }

    if (isOnboarding) {
      trackAnalyticsConnectClicked();
    }
    onConnectClicked?.();
    if (redirectToWebQuickConnect) {
      redirectToWebQuickConnect(args);
    } else {
      displayResultMessage?.(getAuthUrl(args).unwrap()).then((response) => {
        openAuthURL(response);
      });
    }
  };

  const onClickConnectToExchange = () => {
    if (isExternalConnectAccount || getIsFree?.(primaryExchange)) {
      handleConnectToExchange();
    } else {
      handleTriggerClick().then((isQuotaError) => {
        if (isQuotaError) return;
        handleConnectToExchange();
      });
    }
  };

  const handleButtonClick = () => {
    if (noFuturesAccountMessageData?.show)
      return noFuturesAccountMessageData.onSubmit?.();
    else if (quickConnectAlreadyUsedMessageData?.show)
      return quickConnectAlreadyUsedMessageData.onSubmit?.();
    else return onClickConnectToExchange();
  };

  const onButtonClick = () => {
    handleOnSubmit(primaryExchange, handleButtonClick, openConfirmModal);
  };

  const getButtonLoading = () => {
    if (noFuturesAccountMessageData?.show)
      return noFuturesAccountMessageData.loading;
    else if (quickConnectAlreadyUsedMessageData?.show)
      return quickConnectAlreadyUsedMessageData.loading;
    else return getAuthUrlLoading || isLoadingQuotaCheck;
  };

  const onSecondaryButtonClick = () => {
    setTab(ConnectExchangeTabs.API_KEYS);
  };

  const primaryButtonData = {
    text: t("connect_form.connect_to", { exchange: primaryExchange }),
    disabled: accountNameError || !selectedSubExchanges?.length,
    loading: getButtonLoading(),
    onClick: onButtonClick,
  };

  const secondaryButtonData = {
    text: t("connect_form.connect_with_api_keys", {
      exchange: primaryExchange,
    }),
    onClick: onSecondaryButtonClick,
  };

  return {
    quotaModal,
    noFuturesAccountMessageData,
    quickConnectAlreadyUsedMessageData,
    primaryButtonData,
    secondaryButtonData,
  };
};

export default useQuickConnectForm;
